import { useRef, useEffect, useCallback } from "react";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { MeshoptDecoder } from "three/examples/jsm/libs/meshopt_decoder.module.js";

const Avatar3D = ({ isSpeaking = false, isListening = false }) => {
  const containerRef = useRef(null);
  const rendererRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const modelRef = useRef(null);
  const frameRef = useRef(null);
  const timeRef = useRef(0);
  const stateRef = useRef({ isSpeaking: false, isListening: false });

  useEffect(() => {
    stateRef.current = { isSpeaking, isListening };
  }, [isSpeaking, isListening]);

  const animate = useCallback(() => {
    frameRef.current = requestAnimationFrame(animate);
    if (!modelRef.current || !rendererRef.current || !sceneRef.current || !cameraRef.current) return;

    timeRef.current += 0.016;
    const t = timeRef.current;
    const model = modelRef.current;
    const { isSpeaking: sp, isListening: ls } = stateRef.current;

    // Idle floating
    model.position.y = model.userData.baseY + Math.sin(t * 1.2) * 0.008;

    if (sp) {
      model.rotation.y = Math.sin(t * 2.5) * 0.12;
      model.rotation.x = Math.sin(t * 3.5) * 0.04;
      model.position.y += Math.sin(t * 5) * 0.01;
      const pulse = (Math.sin(t * 10) * 0.5 + 0.5) * 0.02;
      model.scale.setY(model.userData.baseScale + pulse);
    } else if (ls) {
      model.rotation.x = -0.06 + Math.sin(t * 1.5) * 0.015;
      model.rotation.y = Math.sin(t * 0.8) * 0.06;
      model.scale.setY(model.userData.baseScale);
    } else {
      model.rotation.y = Math.sin(t * 0.5) * 0.08;
      model.rotation.x = Math.sin(t * 0.3) * 0.015;
      model.scale.setY(model.userData.baseScale);
    }

    rendererRef.current.render(sceneRef.current, cameraRef.current);
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const scene = new THREE.Scene();
    sceneRef.current = scene;

    // Camera framed for head + upper chest, with room for head to extend above
    const camera = new THREE.OrthographicCamera(-0.42, 0.42, 0.7, -0.25, 0.01, 100);
    camera.position.set(0, 0, 3);
    camera.lookAt(0, 0, 0);
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(120, 140);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.5;
    rendererRef.current = renderer;
    container.appendChild(renderer.domElement);

    // Warm studio lighting
    scene.add(new THREE.AmbientLight(0xfff5e0, 1.2));
    const key = new THREE.DirectionalLight(0xffffff, 1.8);
    key.position.set(2, 3, 4);
    scene.add(key);
    const fill = new THREE.DirectionalLight(0xe0e7ff, 0.7);
    fill.position.set(-3, 2, -1);
    scene.add(fill);
    const rim = new THREE.DirectionalLight(0xffd4a0, 1.0);
    rim.position.set(0, 1, -3);
    scene.add(rim);
    scene.add(new THREE.HemisphereLight(0xffeedd, 0x8d7c6b, 0.6));

    const loader = new GLTFLoader();
    loader.setMeshoptDecoder(MeshoptDecoder);
    loader.load("/avatar.glb", (gltf) => {
      const model = gltf.scene;

      const box = new THREE.Box3().setFromObject(model);
      const size = box.getSize(new THREE.Vector3());
      const center = box.getCenter(new THREE.Vector3());
      const maxDim = Math.max(size.x, size.y, size.z);
      const scale = 1.0 / maxDim;
      model.scale.set(scale, scale, scale);
      // Shift up slightly for head focus
      model.position.set(-center.x * scale, -center.y * scale + 0.08, -center.z * scale);
      model.userData.baseY = model.position.y;
      model.userData.baseScale = scale;

      scene.add(model);
      modelRef.current = model;
    });

    animate();

    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
      if (renderer.domElement && container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
      renderer.dispose();
    };
  }, [animate]);

  return <div ref={containerRef} className="flex-shrink-0" style={{ width: 120, height: 140 }} data-testid="avatar-3d" />;
};

export default Avatar3D;
